import gsap from 'gsap';
import { rgba } from './color.js';
import { binCount, frameDuration, heads, loopTurns, rows } from './ring-light.js';

const tau = Math.PI * 2;
const top = -Math.PI / 2;
const degree = Math.PI / 180;
const clumpCount = 24;
const looseShare = 0.3;
const spinSpeed = 0.06;
const opacityRange = { min: 0.1, max: 0.8 };

/**
 * Radial scatter, as a share of the radius. Most particles keep to the `core`
 * band (≈ 20 px on a 160 px radius); a `strayShare` of them drift out as far
 * as `stray` (≈ 56 px across), so the ring's edge is ragged, not ruled.
 */

const scatter = { core: 0.06, stray: 0.18, strayShare: 0.25 };

/** Fade from the resting ring into the light map and back, in seconds. */

const fade = { out: 0.35, in: 0.6 };

/**
 * On hover the resting ring draws in: each particle moves towards the centre
 * by its own share of `reach` (a share of the radius, 16 px on a 160 px one),
 * so the ring tightens unevenly rather than shrinking as a whole. A `stay`
 * share of particles don't move at all; the rest are skewed by `bias`
 * towards small pulls, so only a few travel the full reach. Timed like
 * the retry icon's hover turn (circOut), only slower (1.2 s).
 */

const hover = { reach: 0.1, stay: 0.3, bias: 1.8, duration: 1.2, ease: 'circ.out' };

/**
 * Hovering Ask tints the resting ring: a `share` of the particles, picked at
 * random and spread over the tween, switch to `--magic-ball-ask-palette`.
 * Timed like the hover pull.
 */

const tint = { share: 0.7 };

/**
 * The loading light replays the reference's light map (see ring-light.js);
 * apart from a slow jitter and the band width below, only the particles'
 * brightness moves. `gain` scales the map; particles keep their resting size.
 */

const lightGain = 2.4;

/**
 * Band width while loading, in px on a 160 px radius (it scales with the
 * ring): `head` wide where the comet's peak is, narrowing to `tail` over
 * `reach` degrees behind it. Strays keep only `stray` of their resting reach.
 */

const bandWidth = { head: 44, tail: 16, reach: 130, stray: 0.15 };

const restingWidth = 2 * scatter.core * 160;

/**
 * While loading, particles crowd the middle of the band: each offset is raised
 * to `focus`, which keeps the band's full width but thins its edges out.
 */

const focus = 1.6;

/**
 * Trail shaping on top of the reference: light up to `keep` degrees behind the
 * comet's peak stays as measured; further back it dies off exponentially over
 * `falloff` degrees, so the trail is shorter and fades harder than the
 * reference's. The comet never covers more than half the ring: nothing is lit
 * beyond `lead` degrees ahead of the peak or `length` degrees behind it
 * (`lead + length = 180`), each edge softened over `soft` degrees, so the tail
 * can't wrap round and look as if the head were chasing it.
 */

const trail = { keep: 12, falloff: 28, lead: 20, length: 160, soft: 12 };

/**
 * The same shaping for the knot, when the head stands still at the top: the
 * light gathers into a short clot, as thick across the ring (`head`, px) as
 * it is long, instead of smearing back along the ring. The trail grows from
 * this into `trail` as the head gathers speed (slowly at first, full length
 * from half its top speed up) and shrinks back as it brakes.
 */

const knot = { keep: 1, falloff: 5, lead: 6, length: 12, soft: 5, head: 72 };

/**
 * While loading the frozen particles still jitter, as the reference's do: a
 * quick, out-of-step wobble of up to `angle` radians along the ring and
 * `radius` of the radius across it, at `rate` radians per second of frame time.
 */

const jitter = { angle: 0.015, radius: 0.03, rate: { min: 0.8, max: 2 } };

/**
 * Loading starts just before the reference's knot lights up (2.55 s) and ends
 * in the dark gap after the next knot (2.01–2.55 s, one loop later).
 */

const lightStart = 2.45;

/**
 * The comet makes exactly `turns` full turns, eased in and out. The reference
 * light is only borrowed for its shape: each frame is rotated so its peak sits
 * where `steer` puts the head, not where the reference had it.
 */

const turns = 3;
const turnEase = gsap.parseEase('power2.inOut');

/**
 * Real seconds for each stretch of the reference: the knot lighting up, the
 * run, and the knot braking and going dark. The knots are hurried through, so
 * the ring never hangs at the top. Not scaled by `--magic-ball-speed`, which
 * only paces the resting ring: loading keeps its own time.
 */

const pace = { knotIn: 0.25, run: 4.6, knotOut: 0.35 };

const lightMap = rows.map((row) =>
  Float32Array.from(row, (digit) => 4 * (parseInt(digit, 36) / 35) ** 2)
);
const loopDuration = rows.length * frameDuration;
const binSize = 360 / binCount;

/** Where the reference's knot rests, and when (in reference time) its head leaves and returns to it. */

const restFrame = Math.round(lightStart / frameDuration);
const restHead = heads[restFrame];
const runStart = heads.findIndex((head, index) => index > restFrame && head > restHead + 1) * frameDuration;
const runEnd = loopDuration + heads.findIndex((head) => head >= restHead - 1) * frameDuration;
const lightEnd = loopDuration + 2.07;

/**
 * The brightest bin hops between neighbours from frame to frame, most of all
 * while the knot sits still. Rotating the light by that raw track would shake
 * it sideways, so the track is averaged over `smoothing` frames either side.
 */

const smoothing = 3;

const track = heads.map((_, index) => {
  let sum = 0;

  for (let offset = -smoothing; offset <= smoothing; offset++) {
    const frame = index + offset;
    const lap = Math.floor(frame / heads.length);

    sum += heads[frame - lap * heads.length] + lap * loopTurns * 360;
  }

  return sum / (smoothing * 2 + 1);
});

function spread() {
  return (Math.random() + Math.random() + Math.random() - 1.5) / 1.5;
}

function createClump() {
  return {
    angle: Math.random() * tau,
    width: (2 + Math.random() * 8) * degree,
    weight: 0.3 + Math.random() * 1.2,
    lift: spread() * 0.035,
    sway: 0.02 + Math.random() * 0.05,
    rate: 0.1 + Math.random() * 0.3,
    phase: Math.random() * tau,
  };
}

function pickClump(clumps, total) {
  let roll = Math.random() * total;

  return clumps.find((clump) => (roll -= clump.weight) <= 0) ?? clumps.at(-1);
}

function createParticle(clumps, total, index, count) {
  const offset = spread();
  const clump = Math.random() < looseShare ? null : pickClump(clumps, total);
  const angle = clump ? clump.angle + spread() * clump.width : Math.random() * tau;

  return {
    clump,
    position: (((angle / tau) % 1) + 1) % 1,
    lane: (index + Math.random()) / count,
    rank: Math.random(),
    band: spread() * scatter.core + (clump?.lift ?? 0),
    focus: Math.sign(offset) * Math.abs(offset) ** focus * scatter.core,
    stray: Math.random() < scatter.strayShare ? spread() * scatter.stray : 0,
    drift: 0.004 + Math.random() * 0.02,
    sway: Math.random() * 0.012,
    rate: 0.3 + Math.random() * 0.9,
    phase: Math.random() * tau,
    opacity: opacityRange.min + Math.random() * (opacityRange.max - opacityRange.min),
    size: 0.6 + Math.random() * 1.1,
    tone: Math.random(),
    tint: Math.random(),
    jolt: jitter.rate.min + Math.random() * (jitter.rate.max - jitter.rate.min),
    joltPhase: Math.random() * tau,
    pull: Math.random() < hover.stay ? 0 : Math.random() ** hover.bias,
  };
}

function clumpSway(clump, time) {
  if (!clump) return 0;

  return clump.sway * Math.sin(time * clump.rate + clump.phase);
}

// ─── Light map ────────────────────────────────────────────────────────────────

function sampleRow(row, degrees) {
  const position = (((degrees / binSize - 0.5) % binCount) + binCount) % binCount;
  const index = Math.floor(position);
  const progress = position - index;

  return row[index] * (1 - progress) + row[(index + 1) % binCount] * progress;
}

function frameAt(time) {
  const frame = (((time % loopDuration) + loopDuration) % loopDuration) / frameDuration;
  const index = Math.floor(frame);
  const next = (index + 1) % rows.length;
  const step = track[next] + (next === 0 ? loopTurns * 360 : 0) - track[index];

  return { index, next, step, progress: frame - index };
}

/** Where the comet's peak is at `time`, in degrees clockwise from the top. */

function sampleHead(time) {
  const { index, step, progress } = frameAt(time);

  return track[index] + step * progress;
}

/** Where our head is at `time`: at rest on the knot, then `turns` eased turns back onto it. */

function steer(time) {
  const progress = Math.min(Math.max((time - runStart) / (runEnd - runStart), 0), 1);

  return restHead + turns * 360 * turnEase(progress);
}

function wrapDegrees(value) {
  return ((((value + 180) % 360) + 360) % 360) - 180;
}

/** How far our head is from the reference's peak at `time`, folded into ±180°. */

function gapAt(time) {
  return wrapDegrees(steer(time) - sampleHead(time));
}

const edgeGap = { start: gapAt(runStart), end: gapAt(runEnd) };

/**
 * How far to rotate the reference frame so its peak lands on our head. Outside
 * the run the knot already sits at the top, so the frame is left as it is; the
 * few degrees left between the tracks at either edge are eased out across the
 * run, so the light never steps sideways as it starts or stops.
 */

function shiftAt(time) {
  if (time <= runStart || time >= runEnd) return 0;

  const progress = (time - runStart) / (runEnd - runStart);

  return gapAt(time) - edgeGap.start - (edgeGap.end - edgeGap.start) * progress;
}

/** Band width multiplier at `behind` degrees behind the comet's peak, against the resting core. */

function widthAt(behind, head) {
  const progress = behind > 180 ? 0 : Math.min(behind / bandWidth.reach, 1);
  const eased = progress * progress * (3 - 2 * progress);

  return (head + (bandWidth.tail - head) * eased) / restingWidth;
}

function mix(from, to, progress) {
  return from + (to - from) * progress;
}

/** Trail shape at `time`: `knot` while the head stands still, `trail` once it runs fast enough. */

function trailAt(time) {
  const progress = Math.min(Math.max((time - runStart) / (runEnd - runStart), 0), 1);
  const velocity = progress < 0.5 ? 4 * progress : 4 * (1 - progress);
  const stretch = Math.min(velocity, 1) ** 2;

  return {
    keep: mix(knot.keep, trail.keep, stretch),
    falloff: mix(knot.falloff, trail.falloff, stretch),
    lead: mix(knot.lead, trail.lead, stretch),
    length: mix(knot.length, trail.length, stretch),
    soft: mix(knot.soft, trail.soft, stretch),
    head: mix(knot.head, bandWidth.head, stretch),
  };
}

/** How much of the measured light survives at `behind` degrees behind the peak, for the given trail shape. */

function trailFade(behind, shape) {
  if (behind > 180) {
    const ahead = 360 - behind;

    return Math.min(Math.max((shape.lead - ahead) / shape.soft + 1, 0), 1);
  }

  const edge = Math.min(Math.max((shape.length - behind) / shape.soft, 0), 1);

  if (behind <= shape.keep) return edge;

  return Math.exp(-(behind - shape.keep) / shape.falloff) * edge;
}

/**
 * Reference light at `degrees` clockwise from the top, `time` seconds into the
 * loop. Between two frames each is shifted along the head's motion, so the
 * comet slides instead of cross-fading between two positions.
 */

function sampleLight(time, degrees) {
  const { index, next, step, progress } = frameAt(time);
  const from = sampleRow(lightMap[index], degrees - step * progress);
  const to = sampleRow(lightMap[next], degrees + step * (1 - progress));

  return from * (1 - progress) + to * progress;
}

/**
 * Particle ring. At rest it is a full circle of uneven clumps, spinning
 * slowly. On loading it fades out and the particles freeze in an even band;
 * from then on nothing moves but light, shaped after the reference: a knot
 * lights up at the top, runs three turns as a comet with a fading trail,
 * brakes back into the knot and goes dark. Loading resolves only then, so the
 * result waits for the animation, not a clock. On the result the light fades
 * out and the resting ring fades back in.
 */

export function createRing() {
  const clumps = Array.from({ length: clumpCount }, createClump);
  const total = clumps.reduce((sum, clump) => sum + clump.weight, 0);
  const shape = { mode: 'ring', alpha: 1, crowd: 0, clock: 0, pull: 0, tint: 0 };

  let particles = [];
  let spin = 0;
  let pixel = 1;
  let timeline = null;
  let pending = null;

  /**
   * Rebuilt only when `--magic-ball-particle-count` changes; clumps stay, so the
   * ring keeps its look. Sorted by tone, so the fill colour changes only a few
   * times per frame.
   */

  function populate(count) {
    if (particles.length === count) return;

    particles = Array.from({ length: count }, (_, index) =>
      createParticle(clumps, total, index, count)
    ).sort((first, second) => first.tone - second.tone);
  }

  function toLight() {
    shape.mode = 'light';
    shape.pull = 0;
    shape.tint = 0;
    shape.crowd = 1;
    shape.alpha = 1;
  }

  function toRing() {
    shape.mode = 'ring';
    shape.crowd = 0;
  }

  /** Resolves the loading run in flight, finished or cut short. */

  function finish() {
    pending?.();
    pending = null;
  }

  /**
   * Plays one loading run and resolves when the comet has gone dark. The run
   * is a tween, so it keeps time on GSAP's ticker even while the canvas is
   * off screen and not drawing.
   */

  function start() {
    finish();
    timeline?.kill();
    gsap.killTweensOf(shape);
    shape.clock = lightStart;

    return new Promise((resolve) => {
      pending = resolve;
      timeline = gsap
        .timeline({ onComplete: finish })
        .to(shape, { alpha: 0, duration: fade.out, ease: 'power1.in' })
        .call(toLight)
        .to(shape, { clock: runStart, duration: pace.knotIn, ease: 'none' })
        .to(shape, { clock: runEnd, duration: pace.run, ease: 'none' })
        .to(shape, { clock: lightEnd, duration: pace.knotOut, ease: 'none' });
    });
  }

  /** The light fades out wherever it is, and the resting ring fades back in. */

  function settle() {
    finish();
    timeline?.kill();
    gsap.killTweensOf(shape);

    if (shape.mode === 'ring') {
      timeline = gsap.to(shape, { alpha: 1, duration: fade.in });
      return;
    }

    timeline = gsap
      .timeline()
      .to(shape, { alpha: 0, duration: fade.out, ease: 'power1.in' })
      .call(toRing)
      .to(shape, { alpha: 1, duration: fade.in, ease: 'power1.out' });
  }

  function place(particle, time) {
    const chaos =
      clumpSway(particle.clump, time) +
      Math.sin(time * particle.rate + particle.phase) * particle.drift;

    if (shape.mode === 'ring') return spin + particle.position * tau + chaos;

    return (
      top +
      particle.lane * tau +
      chaos +
      Math.sin(time * particle.jolt + particle.joltPhase) * jitter.angle
    );
  }

  return {
    resize(frame) {
      pixel = Math.max(frame.radius / 160, 1);
    },

    /** Draws the resting ring in towards the centre, or lets it back out. */

    setHover(on) {
      gsap.to(shape, {
        pull: on ? 1 : 0,
        duration: hover.duration,
        ease: hover.ease,
        overwrite: 'auto',
      });
    },

    /** Tints part of the resting ring in the Ask palette, or lets it fade back. */

    setTint(on) {
      gsap.to(shape, {
        tint: on ? tint.share : 0,
        duration: hover.duration,
        ease: hover.ease,
        overwrite: 'auto',
      });
    },

    setState(name, frame) {
      if (name === 'loading' && !frame.still) return start();

      settle();

      return null;
    },

    destroy() {
      finish();
      timeline?.kill();
      gsap.killTweensOf(shape);
    },

    render(context, frame) {
      const { width, height, radius, time, delta, settings } = frame;
      const centerX = width / 2;
      const centerY = height / 2;
      const loading = shape.mode === 'light';
      const strength = settings.intensity * shape.alpha * (loading ? lightGain : 1);
      const { clock } = shape;

      populate(Math.max(Math.round(settings.count), 1));
      spin += delta * spinSpeed;

      if (strength <= 0) return;

      const head = loading ? steer(clock) : 0;
      const shift = loading ? shiftAt(clock) : 0;
      const tail = loading ? trailAt(clock) : null;
      const palettes = [settings.palette, settings.askPalette].map((palette) =>
        palette.map((color) => rgba(color, 1))
      );

      let tone = null;

      particles.forEach((particle) => {
        const visibility = particle.rank >= settings.sparsity ? 1 : shape.crowd;

        if (visibility <= 0) return;

        const angle = place(particle, time);
        const degrees = (angle - top) / degree;
        const behind = (((head - degrees) % 360) + 360) % 360;
        const lit = loading ? sampleLight(clock, degrees - shift) * trailFade(behind, tail) : 1;
        const alpha = Math.min(particle.opacity * visibility * strength * lit, 1);

        if (alpha < 0.01) return;

        const wobble = time * particle.rate + particle.phase;
        const distance =
          radius *
          (1 +
            (loading
              ? (particle.focus + particle.stray * bandWidth.stray) * widthAt(behind, tail.head)
              : particle.band + particle.stray) +
            Math.cos(wobble * 0.7) * particle.sway +
            (loading
              ? Math.cos(time * particle.jolt * 1.3 + particle.joltPhase) * jitter.radius
              : -particle.pull * shape.pull * hover.reach));
        const size = particle.size * pixel;

        const tones = palettes[particle.tint < shape.tint ? 1 : 0];
        const index = Math.min(Math.floor(particle.tone * tones.length), tones.length - 1);

        if (tones[index] !== tone) {
          tone = tones[index];
          context.fillStyle = tone;
        }

        context.globalAlpha = alpha;
        context.fillRect(
          centerX + Math.cos(angle) * distance - size / 2,
          centerY + Math.sin(angle) * distance - size / 2,
          size,
          size
        );
      });

      context.globalAlpha = 1;
    },
  };
}
